import React, { FC, ReactNode } from 'react';
import classNames from 'classnames';
import style from '../inputs.module.css';
import AppGetIcon from '../../AppGetIcon';

export type FieldStatus =
  | 'default'
  | 'error'
  | 'success'
  | 'incomplete'
  | 'complete'
  | 'pressed'
  | 'readonly'
  | 'disabled'
  | 'loading';

interface BaseFieldProps {
  label?: string;
  error?: string;
  touched?: boolean;
  status?: FieldStatus | string;
  children: ReactNode;
  onClear?: () => void;
  /** Color hexadecimal para el círculo indicador */
  indicatorColor?: string;
}

const BaseField: FC<BaseFieldProps> = ({
  label,
  error,
  touched,
  status,
  children,
  onClear,
  indicatorColor,
}) => {
  const containerClassName = classNames(style.container__input, {
    [style.errorInput]: status === 'error',
    [style.successInput]: status === 'success',
    [style.incompleteInput]: status === 'incomplete',
    [style.completeInput]: status === 'complete',
    [style.pressedInput]: status === 'pressed',
    [style.disabledInput]: ['readonly', 'disabled', 'loading'].includes(status || 'default'),
  });

  const isValidHex =
    typeof indicatorColor === 'string' &&
    /^#(?:[0-9A-Fa-f]{3}){1,2}$/.test(indicatorColor);

  // 1) Saber si hay icono a la derecha
  const hasIcon = ['error','success','incomplete','complete','pressed','loading'].includes(status || '');

  // 2) Ajustar padding del input cuando haya indicador de color y/o icono
  const modifiedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child)) {
      const baseStyle = child.props.style || {};
      let extraPadding: React.CSSProperties = {};
      if (isValidHex && hasIcon) {
        extraPadding = { paddingRight: '4.5rem' };
      } else if (isValidHex) {
        extraPadding = { paddingRight: '2.5rem' };
      }
      return React.cloneElement(child as React.ReactElement<any>, {
        style: { ...baseStyle, ...extraPadding },
      });
    }
    return child;
  });

  const renderIcon = () => {
    const iconWrapperStyle: React.CSSProperties = {
      position: 'absolute',
      right: '-7px',
      top: '50%',
      transform: 'translateY(-50%)',
      color: 'red',
    };

    switch (status) {
      case 'error':
        return (
          <div style={iconWrapperStyle}>
            <AppGetIcon
              classIcon={style.clearButtonError}
              onClick={onClear}
              name="error"
              strokeColor="var(--color-on-surface-main)"
            />
          </div>
        );
      case 'success':
        return (
          <div style={iconWrapperStyle}>
            <AppGetIcon
              classIcon={style.clearButtonSuccess}
              onClick={onClear}
              name="error"
              strokeColor="var(--color-on-surface-main)"
            />
          </div>
        );
      case 'incomplete':
        return (
          <div style={iconWrapperStyle}>
            <AppGetIcon
              classIcon={style.clearButtonIncomplete}
              name="iconAlert"
              strokeColor="var(--color-on-surface-main)"
            />
          </div>
        );
      case 'complete':
        return (
          <div style={iconWrapperStyle}>
            <AppGetIcon
              classIcon={style.alertButton}
              name="iconAlert"
              strokeColor="var(--color-on-surface-main)"
            />
          </div>
        );
      case 'pressed':
        return <button className={style.checkButton}>✔</button>;
      case 'loading':
        return <div className={style.spinner}></div>;
      default:
        return null;
    }
  };

  return (
    <div className={containerClassName}>
      {label && <label className={style.input__label}>{label}</label>}
      <div className={style.inputWrapper} style={{ position: 'relative' }}>
        {modifiedChildren}

        {/* Indicador de color con desplazamiento condicional */}
        {isValidHex && (
          <span
            className={style.colorCircle}
            style={{
              backgroundColor: indicatorColor,
              right: hasIcon ? '2.75rem' : '0.75rem',
            }}
          />
        )}

        {/* Icono de estado */}
        {renderIcon()}
      </div>
      {touched && error && <div className={style.error}>{error}</div>}
    </div>
  );
};

export default BaseField;